import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useReactToPrint } from "react-to-print";
import { Search, Printer, Trash2, Download, MessageCircle, X } from "lucide-react";
import { downloadPdf } from "@/lib/pdf";
import { openWhatsApp } from "@/lib/whatsapp";
import { money, numeroALetras, fechaMX } from "@/lib/format";
import ReporteModulo from "@/components/ReporteModulo";
import FiltroSelect, { labelDe } from "@/components/FiltroSelect";

const REM_EST = ["Pendiente", "En proceso", "Surtido", "Enviado", "Entregado"];
const remNumero = (r) => (r.numero_remision ? r.numero_remision.replace(/^(PED|COT|CS)-/, "REM-") : "-");

export default function PedidosSurtir() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [estSel, setEstSel] = useState("todos");
  const visibles = useMemo(() => rows.filter((r) => (estSel === "todos" || (r.estatus || "Pendiente") === estSel) && (!search || `${remNumero(r)} ${r.cliente} ${r.municipio || ""} ${r.estatus || ""}`.toLowerCase().includes(search.toLowerCase()))), [rows, estSel, search]);
  const printRef = useRef(null);

  const fetch = async () => {
    try { setLoading(true); const { data } = await api.get("/remisiones"); setRows(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const cambiarEstatus = async (id, estatus) => {
    try { await api.patch(`/remisiones/${id}/estatus`, { estatus }); setRows((p) => p.map((r) => (r.id === id ? { ...r, estatus } : r))); toast.success("Estatus actualizado"); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta remisión?")) return;
    try { await api.delete(`/remisiones/${id}`); setRows((p) => p.filter((r) => r.id !== id)); toast.success("Remisión eliminada"); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };

  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: sel ? remNumero(sel) : "Remision" });

  const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const grupoFecha = (r) => r.fecha || (r.created_at ? String(r.created_at).slice(0, 10) : null);

  const grupos = useMemo(() => {
    const now = new Date();
    const curY = now.getFullYear();
    const curM = now.getMonth();
    const filtrados = visibles;
    const sortDesc = (a, b) => (grupoFecha(b) || "").localeCompare(grupoFecha(a) || "");
    const mesEnCurso = [], mesesAnio = {}, porAnio = {}, sinFecha = [];
    filtrados.forEach((r) => {
      const f = grupoFecha(r);
      const d = f ? new Date(f + "T00:00:00") : null;
      if (!d || isNaN(d)) { sinFecha.push(r); return; }
      const y = d.getFullYear(), m = d.getMonth();
      if (y === curY && m === curM) mesEnCurso.push(r);
      else if (y === curY) (mesesAnio[m] = mesesAnio[m] || []).push(r);
      else if (y < curY) { (porAnio[y] = porAnio[y] || {}); (porAnio[y][m] = porAnio[y][m] || []).push(r); }
      else (mesesAnio[m] = mesesAnio[m] || []).push(r);
    });
    mesEnCurso.sort(sortDesc); sinFecha.sort(sortDesc);
    Object.values(mesesAnio).forEach((a) => a.sort(sortDesc));
    Object.values(porAnio).forEach((mm) => Object.values(mm).forEach((a) => a.sort(sortDesc)));
    return { mesEnCurso, mesesAnio, porAnio, sinFecha, curY };
  }, [visibles]);
  const mesesAnioKeys = Object.keys(grupos.mesesAnio).map(Number).sort((a, b) => a - b);
  const countMesesAnio = mesesAnioKeys.reduce((s, m) => s + grupos.mesesAnio[m].length, 0);
  const aniosKeys = Object.keys(grupos.porAnio).sort((a, b) => String(b).localeCompare(String(a)));
  const countAnios = aniosKeys.reduce((s, y) => s + Object.values(grupos.porAnio[y]).reduce((t, a) => t + a.length, 0), 0);

  const Seccion = ({ titulo, rows: filas }) => (
    <div className="space-y-2" data-testid={`remision-seccion-${titulo.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">{titulo}</h3>
        <Badge variant="secondary">{filas.length}</Badge>
      </div>
      {filas.length === 0 ? (
        <p className="text-sm text-muted-foreground pb-2">No hay remisiones en este periodo.</p>
      ) : (
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Remisión</TableHead><TableHead>Cliente</TableHead><TableHead>Municipio</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead><TableHead className="w-44">Estatus</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
            <TableBody>
              {filas.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs font-semibold">{remNumero(r)}{r.tipo === "complemento" && <Badge className="ml-2 bg-amber-100 text-amber-800 border-transparent font-sans" data-testid={`rem-complemento-${r.id}`}>Complemento</Badge>}</TableCell>
                  <TableCell className="font-medium">{r.cliente}</TableCell>
                  <TableCell>{r.municipio || "-"}</TableCell>
                  <TableCell>{r.estado_geo || "-"}</TableCell>
                  <TableCell>{fechaMX(r.fecha)}</TableCell>
                  <TableCell>
                    {r.estatus === "Entregado" ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-transparent" title="La entrega se controla desde Pedidos">Entregado</Badge>
                    ) : isAdmin ? (
                      <Select value={r.estatus || "Pendiente"} onValueChange={(v) => cambiarEstatus(r.id, v)}>
                        <SelectTrigger className="h-8" data-testid={`remision-estatus-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pendiente">Pendiente</SelectItem>
                          <SelectItem value="En proceso">En proceso</SelectItem>
                          <SelectItem value="Surtido">Surtido</SelectItem>
                          <SelectItem value="Enviado">Enviado</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : <Badge>{r.estatus || "Pendiente"}</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="icon" title="Ver / Imprimir" onClick={() => setSel(r)} data-testid={`remision-ver-${r.id}`}><Printer className="h-4 w-4" /></Button>
                      {isAdmin && <Button variant="destructive" size="icon" title="Eliminar" onClick={() => eliminar(r.id)}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  const cl = sel?.clientes;
  const usarCliente = cl?.entrega_usar_datos_cliente;
  const dir = cl ? (usarCliente
    ? `${cl.calle || ""} ${cl.colonia || ""}, ${cl.municipio_alcaldia || ""}, ${cl.estado || ""} ${cl.codigo_postal || ""}`
    : `${cl.entrega_calle_numero || ""} ${cl.entrega_colonia || ""}, ${cl.entrega_municipio || ""}, ${cl.entrega_estado || ""} ${cl.entrega_codigo_postal || ""}`) : "";
  const esComp = sel?.tipo === "complemento" && Array.isArray(sel?.partidas);
  const partidas = esComp ? sel.partidas : sel?.cotizaciones?.partidas || [];
  const totalKg = partidas.reduce((a, p) => a + (Number(p.peso) || 0), 0);
  const totalAtados = partidas.reduce((a, p) => a + (Number(p.atados) || 0), 0);
  const cot = esComp ? null : sel?.cotizaciones;

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="pedidos-surtir-page">
      <div><h1 className="text-3xl font-bold tracking-tight">Pedidos por surtir</h1><p className="text-muted-foreground mt-2">Remisiones de embarque y despacho.</p></div>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Buscar por remisión, cliente, municipio o estatus..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="remision-search" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
            <FiltroSelect value={estSel} onChange={setEstSel} todosLabel="Todos los estatus" options={REM_EST} testid="remision-filtro-estatus" className="w-48" />
            {isAdmin && (
            <ReporteModulo
              titulo="REPORTE DE PEDIDOS POR SURTIR"
              filenameBase="Reporte pedidos por surtir"
              prefix="rep-rem"
              triggerTestId="remision-reporte-btn"
              buttonVariant="outline"
              rows={visibles}
              getFecha={grupoFecha}
              filtros={[{ label: "Estatus", value: labelDe(estSel, REM_EST) }, { label: "Búsqueda", value: search }]}
              columns={[
                { header: "Remisión", value: (r) => remNumero(r), mono: true },
                { header: "Cliente", value: (r) => r.cliente },
                { header: "Municipio", value: (r) => r.municipio || "-" },
                { header: "Estado", value: (r) => r.estado_geo || "-" },
                { header: "Fecha", value: (r) => fechaMX(r.fecha) },
                { header: "Estatus", value: (r) => r.estatus || "Pendiente" },
              ]}
            />
            )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <p className="text-center text-muted-foreground py-12">Cargando...</p>
          ) : (
            <>
              <Seccion titulo="Mes en curso" rows={grupos.mesEnCurso} />

              <Accordion type="multiple" className="border rounded-md divide-y">
                <AccordionItem value="meses-anio" className="border-0 px-4">
                  <AccordionTrigger data-testid="acc-meses-anteriores" className="hover:no-underline">
                    <span className="flex items-center gap-2"><span className="text-lg font-semibold">Meses anteriores ({grupos.curY})</span><Badge variant="secondary">{countMesesAnio}</Badge></span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-6 pt-2">
                    {mesesAnioKeys.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No hay remisiones en meses anteriores de este año.</p>
                    ) : (
                      mesesAnioKeys.map((m) => <Seccion key={m} titulo={`${MESES[m]} ${grupos.curY}`} rows={grupos.mesesAnio[m]} />)
                    )}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="anios" className="border-0 px-4">
                  <AccordionTrigger data-testid="acc-anios-anteriores" className="hover:no-underline">
                    <span className="flex items-center gap-2"><span className="text-lg font-semibold">Años anteriores</span><Badge variant="secondary">{countAnios}</Badge></span>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2">
                    {aniosKeys.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No hay remisiones de años anteriores.</p>
                    ) : (
                      <Accordion type="multiple" className="border rounded-md divide-y">
                        {aniosKeys.map((y) => {
                          const mesesKeys = Object.keys(grupos.porAnio[y]).map(Number).sort((a, b) => a - b);
                          const countY = mesesKeys.reduce((s, m) => s + grupos.porAnio[y][m].length, 0);
                          return (
                            <AccordionItem key={y} value={y} className="border-0 px-4">
                              <AccordionTrigger data-testid={`acc-anio-${y}`} className="hover:no-underline">
                                <span className="flex items-center gap-2"><span className="font-semibold">{y}</span><Badge variant="secondary">{countY}</Badge></span>
                              </AccordionTrigger>
                              <AccordionContent className="space-y-6 pt-2">
                                {mesesKeys.map((m) => <Seccion key={m} titulo={`${MESES[m]} ${y}`} rows={grupos.porAnio[y][m]} />)}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {grupos.sinFecha.length > 0 && <Seccion titulo="Sin fecha" rows={grupos.sinFecha} />}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-5xl max-h-[90dvh] overflow-y-auto overflow-x-auto [&>button.absolute]:hidden">
          <DialogHeader>
            <div className="flex justify-between items-center pr-8">
              <div><DialogTitle>Remisión {sel && remNumero(sel)}</DialogTitle><DialogDescription>{sel?.cliente}</DialogDescription></div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" size="sm" className="text-green-600 border-green-600 hover:bg-green-50 hover:text-green-700" onClick={() => { downloadPdf(printRef.current, sel ? remNumero(sel) : "Remision"); openWhatsApp("", `Hola ${cl?.nombre_contacto || sel?.cliente || ""}, le compartimos la remisión ${sel ? remNumero(sel) : ""} de Duelas Cooper Slip${cot?.total ? ` por un total de ${money(cot.total)} (IVA incluido)` : ""}. Le adjuntamos el PDF. ¡Gracias!`); }} data-testid="remision-whatsapp"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp</Button>
                <Button variant="outline" size="sm" onClick={() => downloadPdf(printRef.current, sel ? remNumero(sel) : "Remision")} data-testid="remision-descargar-pdf"><Download className="h-4 w-4 mr-2" /> Descargar PDF</Button>
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="remision-imprimir"><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
                <Button variant="outline" size="sm" onClick={() => setSel(null)} data-testid="remision-cerrar"><X className="h-4 w-4 mr-2" /> Cerrar</Button>
              </div>
            </div>
          </DialogHeader>
          {sel && (() => {
            const m = (v) => "$" + Number(v || 0).toFixed(2);
            const fechaExp = sel.fecha || "";
            let fechaVenc = fechaExp;
            const dc = Number(cl?.dias_credito || 0);
            if (fechaExp && dc > 0) {
              const d = new Date(fechaExp); d.setDate(d.getDate() + dc);
              fechaVenc = d.toISOString().slice(0, 10);
            }
            return (
            <div ref={printRef} className="bg-white text-black px-6 py-6 md:px-8 md:py-8 w-full max-w-[750px] mx-auto box-border">
              <style>{`@media print{@page{size:letter;margin:12mm}body{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff!important}}.pdf-exporting table td,.pdf-exporting table th{line-height:1.3!important;padding-top:5px!important;padding-bottom:5px!important;vertical-align:middle!important}.pdf-exporting table td{font-size:10.5px!important}`}</style>
              <div className="grid grid-cols-[auto_1fr_auto] gap-6 items-start bg-gray-50 px-3 py-3">
                <div className="text-center">
                  <img src="/logo-cooper.png" alt="Cooper Slip" className="h-16 w-16 object-contain rounded-lg mx-auto" />
                  <div className="text-xs mt-1">Cooper Slip</div>
                </div>
                <div className="text-center text-sm font-bold leading-snug pt-3">
                  <p>Cuando tu cliente queda bien, Tu quedas mejor.</p>
                  <p>Elije las duelas que respaldan a tu negocio</p>
                </div>
                <div className="text-right">
                  <h2 className="text-3xl font-extrabold tracking-tight">REMISIÓN</h2>
                  <p className="text-sm mt-1"><span className="font-bold">No.:</span> {remNumero(sel)}</p>
                  <p className="text-sm"><span className="font-bold">Fecha:</span> {fechaMX(sel.fecha)}</p>
                </div>
              </div>
              <div className="border-t-4 border-black" />
              <div className="mt-4">
                <h3 className="font-bold text-base mb-1">Datos del Cliente</h3>
                <p className="text-sm"><span className="font-bold">Cliente:</span> {cl?.entrega_razon_social || cl?.nombre_razon_social || sel.cliente}</p>
                {(cl?.entrega_nombre_recibe || cl?.nombre_contacto) && <p className="text-sm"><span className="font-bold">Recibe:</span> {cl?.entrega_nombre_recibe || cl?.nombre_contacto}</p>}
                {(() => {
                  const partes = [cl?.entrega_calle_numero, cl?.entrega_colonia, cl?.entrega_municipio, `${cl?.entrega_estado || ""} ${cl?.entrega_codigo_postal || ""}`.trim()].filter((x) => x && String(x).trim());
                  const eDir = partes.join(", ");
                  return eDir ? <p className="text-sm"><span className="font-bold">Dirección de entrega:</span> {eDir}</p> : null;
                })()}
                {(cl?.entrega_telefono || cl?.telefono) && <p className="text-sm"><span className="font-bold">Teléfono:</span> {cl?.entrega_telefono || cl?.telefono}</p>}
              </div>
              <table className="w-full text-xs border-collapse mt-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-2 py-1 align-middle text-left">N/REF</th>
                    <th className="border border-gray-300 px-2 py-1 align-middle text-left">Modelos</th>
                    <th className="border border-gray-300 px-2 py-1 align-middle text-center">Cantidad</th>
                    <th className="border border-gray-300 px-2 py-1 align-middle text-center">Panel</th>
                    <th className="border border-gray-300 px-2 py-1 align-middle text-center">Atados</th>
                    <th className="border border-gray-300 px-2 py-1 align-middle text-center">Peso</th>
                  </tr>
                </thead>
                <tbody>
                  {partidas.map((p, i) => (
                    <tr key={i}>
                      <td className="border border-gray-300 px-2 py-1 align-middle">{p.nRef || p.numero_referencia || "-"}</td>
                      <td className="border border-gray-300 px-2 py-1 align-middle">{p.modelo}</td>
                      <td className="border border-gray-300 px-2 py-1 align-middle text-center">{p.cantidad}</td>
                      <td className="border border-gray-300 px-2 py-1 align-middle text-center">{p.tipo || "Verde"}</td>
                      <td className="border border-gray-300 px-2 py-1 align-middle text-center">{p.atados != null ? Number(p.atados).toFixed(0) : "-"}</td>
                      <td className="border border-gray-300 px-2 py-1 align-middle text-center">{Number(p.peso || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 text-sm">
                <p><span className="font-bold">Total de Atados:</span> {totalAtados.toFixed(0)}</p>
                <p><span className="font-bold">Total de KG:</span> {totalKg.toFixed(2)} kg</p>
              </div>
              {cot && (
                <div className="border border-black rounded-md p-4 mt-6 text-sm">
                  <h3 className="font-bold mb-2">PAGARÉ</h3>
                  <p className="leading-relaxed">
                    Por valor recibido de <strong>{m(cot.total)}</strong> ( <strong>{numeroALetras(cot.total)}</strong> ), prometo(emos) pagar incondicionalmente a la orden de <strong>Alsea Port Line, S.A. de C.V.</strong> en <strong>Naucalpan, Estado de México</strong>, el día <strong>{fechaVenc || "-"}</strong>.
                  </p>
                  <p className="mt-2 leading-relaxed">Este pagaré causa intereses moratorios a razón del <strong>4% mensual</strong> a partir de su fecha de vencimiento.</p>
                  <p className="mt-2 leading-relaxed">Lugar y Fecha de Expedición: <strong>Naucalpan, Estado de México, a {fechaExp || "-"}</strong>.</p>
                  <p className="mt-8">Nombre persona autorizada para recibir (Deudor), Fecha, Firma y Sello de la empresa</p>
                </div>
              )}
              <p className="text-center italic text-sm mt-6">Con este Importe No solo compras duelas, compras tranquilidad y reputacion frente a tus clientes</p>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
